// The composed dashboard analytics read — ONE band-scoped query that feeds every analytics
// widget (trending / streaming+socials / top-cities / recent-release). Hand-written over the generated client via
// the tenant-keyed wrapper: keyed ['band', bandId, 'dashboard-stats'] so a band switch scorches it AND
// the `sync-completed` plane-B signal invalidates it live (merged with the connections handler). Served
// straight from the analytics.* projections — NO vendor call on this read path; the numbers are the SAME
// per-artist projections the Music tab reads.
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type DashboardAnalytics = components["schemas"]["DashboardAnalytics"];
export type StatMover = components["schemas"]["StatMover"];
export type SocialTile = components["schemas"]["SocialTile"];
export type TopCityRow = components["schemas"]["TopCityRow"];
export type TopCitiesBlock = components["schemas"]["TopCitiesBlock"];

/** GET the composed dashboard analytics (band:read). `staleTime` ~60s per the stats domain.
 *  `enabled: false` for a caller that already HOLDS this projection — the roster heartbeat takes
 *  `stats.TopCities` off its own composed read, so re-fetching would be a round-trip for data in hand. */
export function useDashboardStats({ enabled = true }: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<DashboardAnalytics>(
    ["dashboard-stats"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/dashboard/analytics", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000, enabled },
  );
}
